import React, { useCallback, useMemo, memo } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { useParams, useNavigate } from 'react-router-dom'
import CopyToClipboard from 'react-copy-to-clipboard'
import { postLikeProducts } from '../../api/product'
import { plusComma } from '../../js/script'
import { AFWebLinkType, handlePostMessage, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { addToWishlist } from '../../js/pixelEvent'
import { useAppContext } from '../provider/AppProvider'
import { useMyContext } from '../provider/MyProvider'
import { useCommonContext } from '../provider/CommonProvider'
import ProductCouponCenterModal from './ProductCouponCenterModal'
import { BtnCouponDownload } from '../Button'
import { ProductDetailType, ProductDetailBaseInfo, ProductDetailCategory2 } from '../../@types/product'
import { GTMItems } from '../../@types/dataLayerObject'
import { IssuableCouponType } from '../../@types/coupon'
import ProductDiscountInfo from './ProductDiscountInfo'

interface ProductPriceInfoPCProps {
  product: ProductDetailType
  productItem: ProductDetailBaseInfo | undefined
  prevItems: GTMItems[]
  couponSort: IssuableCouponType[]
  couponDown: boolean
  setCouponDown: React.Dispatch<React.SetStateAction<boolean>>
  setCouponCheck: React.Dispatch<React.SetStateAction<boolean>>
  disCountAcc: number
  wish: boolean
  setWish: React.Dispatch<React.SetStateAction<boolean>>
  osCheck: string
}

// 이미지 경로
const imgURL = '/images'

const ProductPriceInfoPC: React.FC<ProductPriceInfoPCProps> = ({
  product,
  productItem,
  prevItems,
  couponSort,
  couponDown,
  setCouponDown,
  setCouponCheck,
  disCountAcc,
  wish,
  setWish,
  osCheck,
}) => {
  const starCountArray: number[] = [0, 0, 0, 0, 0]

  const param = useParams()
  const navigate = useNavigate()
  const { isAppView } = useAppContext()
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { setIsLogin, hasAccessToken } = useCommonContext()

  const handleCopy = useCallback(
    async (url: string) => {
      try {
        if (osCheck !== 'Android OS') {
          setIsToast(true)
          setToastMessage('URL이 복사되었습니다.')
        }
      } catch (err) {
        console.error('Failed to copy: ', '복사안됨', err)
      }
    },
    [osCheck, setIsToast, setToastMessage]
  )

  const handleShare = useCallback(
    (url: string) => {
      const eventInfo = {
        method: `${url}&direct=true`,
        item_id: productItem?.productNo,
        item_name: productItem?.productName,
      }

      const shareClick: DataLayerArgs = {
        dataLayer: {
          event: 'share',
          event_info: eventInfo,
        },
      }

      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'share',
          eventType: eventInfo,
        })
      } else {
        TagManager.dataLayer(shareClick)
      }
    },
    [isAppView, productItem]
  )

  const onShareClick = useCallback(() => {
    const url = `${window.location.href}?app=${isAppView}`
    handlePostMessage({
      actionType: 'shareUrl',
      params: { url },
    })
    handleShare(url)
  }, [isAppView, handleShare])

  const onShareCopy = useCallback(() => {
    const url = `${window.location.href}?app=${isMobile}`
    handleCopy(url)
    handleShare(url)
  }, [handleCopy, handleShare])

  const createAddWishItem = useCallback(
    (
      product: ProductDetailType,
      productItem: ProductDetailBaseInfo,
      prevItems: GTMItems[],
      productArray: ProductDetailCategory2[]
    ) => ({
      dataLayer: {
        event: 'add_to_wishlist',
        ecommerce: {
          currency: 'KRW',
          value: product.price?.salePrice - product.price?.immediateDiscountAmt,
          pay_back: product.price?.accumulationAmtWhenBuyConfirm,
          reserve_discount: 0,
          items: [
            {
              item_id: productItem?.productNo,
              item_name: productItem?.productName,
              affiliation: '어바웃피싱',
              item_list_price: product.price?.salePrice,
              item_selling_price: product.price?.salePrice - product.price?.immediateDiscountAmt,
              price: product.price?.salePrice - product.price?.immediateDiscountAmt,
              quantity: 1,
              item_total_discount: product.price?.immediateDiscountAmt,
              discount: 0,
              index: prevItems[0]?.index,
              item_brand: product.brand?.name,
              item_category: prevItems[0]?.item_category || productArray[0].categories[0]?.label,
              item_category2: prevItems[0]?.item_category2 || productArray[0].categories[1]?.label,
              item_category3: prevItems[0]?.item_category3 || productArray[0].categories[2]?.label,
              item_variant: undefined,
              item_list_id: prevItems[0]?.item_list_id || productArray[0]?.categories[1]?.categoryNo,
              item_list_name: prevItems[0]?.item_list_name || productArray[0]?.categories[1]?.label,
              promotion_id: prevItems[0]?.promotion_id || '',
              promotion_name: prevItems[0]?.promotion_name || '',
            },
          ],
        },
      },
    }),
    []
  )

  const handleLoginRequired = useCallback(() => {
    setIsToast(true)
    setToastMessage('로그인이 필요한 서비스입니다.')
    setToastBtnMessage('로그인')
    setToastEvent(() => () => setIsLogin(true))
  }, [setIsToast, setToastMessage, setToastBtnMessage, setToastEvent, setIsLogin])

  const handleAddToWishlist = useCallback(
    (res: any) => {
      if (!productItem) return
      if (res.data.length === 0) return

      setWish((prev: boolean) => !prev)

      const productArray = product.categories.filter((el: any) => el.categories[0].label !== '브랜드')
      const addWishItem = createAddWishItem(product, productItem, prevItems, productArray)

      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'add_to_wishlist',
          eventType: addWishItem.dataLayer.ecommerce,
        })
      } else {
        TagManager.dataLayer(addWishItem)
      }

      addToWishlist(
        productItem.productNo,
        productItem.productName,
        product.price?.salePrice,
        product.price?.immediateDiscountAmt
      )

      setIsToast(true)
      setToastMessage(res.data[0].like === 'Y' ? '찜 리스트에 저장되었습니다.' : '찜 리스트에 제외되었습니다.')
      setToastBtnMessage('바로가기')
      setToastEvent(() => () => {
        if (isAppView) {
          RNPostMessage(AFWebLinkType.moveToLink, {
            url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/wishList`,
          })
        } else {
          navigate('/wishList')
        }
      })
    },
    [
      product,
      productItem,
      prevItems,
      createAddWishItem,
      isAppView,
      setWish,
      setIsToast,
      setToastMessage,
      setToastBtnMessage,
      setToastEvent,
      navigate,
    ]
  )

  const onWishlistClick = useCallback(async () => {
    if (!hasAccessToken) {
      handleLoginRequired()
      return
    }

    if (product?.categories[0].fullCategoryLabel === '100원샵') {
      alert('100원샵 상품은 찜하기가 안됩니다.')
      return
    }

    if (!productItem) return

    const likeItem = {
      like: wish ? 'N' : 'Y',
      productNo: param.id,
    }

    try {
      const res = await postLikeProducts(likeItem)
      handleAddToWishlist(res)
    } catch (err) {
      console.log(err)
    }
  }, [hasAccessToken, handleLoginRequired, product, productItem, wish, param.id, handleAddToWishlist])

  // NOTE: 별점 출력
  const renderStar = useCallback((index: number, reviewRate: number) => {
    const starTypes = {
      full: `${imgURL}/star.svg`,
      half: `${imgURL}/star_half_off.svg`,
      off: `${imgURL}/star_off.svg`,
    }

    const isHalfStar = reviewRate - index === 0.5
    const isFullStar = index + 1 <= Math.ceil(reviewRate)
    const src = isFullStar ? (isHalfStar ? starTypes.half : starTypes.full) : starTypes.off

    return <img src={src} alt='별점' key={index} />
  }, [])

  // NOTE: 할인된 가격 출력
  const renderDiscountedPrice = useCallback((product: any) => {
    const discountPercentage = product.price
      ? Math.floor((product.price.immediateDiscountAmt / product.price.salePrice) * 100)
      : 0
    const discountedPrice = product.price ? plusComma(product.price.salePrice - product.price.immediateDiscountAmt) : 0
    const salePrice = product.price ? plusComma(product.price.salePrice) : 0

    return discountPercentage === 0 ? (
      <div className='discounted_price'>
        <p>{discountedPrice}원</p>
      </div>
    ) : (
      <>
        <div className='sale_price'>{salePrice}원</div>
        <div className='discounted_price'>
          <p className='percent'>{discountPercentage}%</p>
          <p>{discountedPrice}원</p>
        </div>
      </>
    )
  }, [])

  const renderMileage = useCallback(
    (product: any) => {
      if (!hasAccessToken || product.price?.accumulationAmtWhenBuyConfirm === 0) return null
      return (
        <div className='save_mileage'>
          <p>페이백 적립금</p>
          <p>{plusComma(product.price?.accumulationAmtWhenBuyConfirm) || 0}</p>
          <img src={`${imgURL}/icon_point.png`} alt='' />
        </div>
      )
    },
    [hasAccessToken]
  )

  const shareButton = useMemo(() => {
    return isAppView ? (
      <button>
        <img src={`${imgURL}/icon_share.png`} alt='공유하기' onClick={onShareClick} />
      </button>
    ) : (
      <CopyToClipboard text={`${window.location.href}?app=${isMobile}`} onCopy={onShareCopy}>
        <button>
          <img src={`${imgURL}/icon_share.png`} alt='공유하기' />
        </button>
      </CopyToClipboard>
    )
  }, [isAppView, onShareClick, onShareCopy])

  return (
    <div className='info'>
      {/* 제품 상단 정보 */}
      <div className='top_info'>
        {/* 제목 wrap */}
        <div className='title_wrap'>
          {/* 브랜드 제목 */}
          <div className='brand_title'>{product.brand?.name}</div>
          {/* 상품 제목 */}
          <div className='title'>{productItem?.productName}</div>
        </div>

        {/* 공유하기, 찜하기 아이콘 */}
        <div className='detail_icons'>
          {/* 공유하기 */}
          {shareButton}

          {/* 찜하기 */}
          <button onClick={onWishlistClick}>
            <img src={wish ? `${imgURL}/icon_heart_fill.png` : `${imgURL}/icon_heart_outline.png`} alt='찜하기' />
          </button>
        </div>
      </div>

      {/* 별점, 리뷰 */}
      <div className='star_rating_detail'>
        <p className='star_rating'>{starCountArray.map((_, idx) => renderStar(idx, product.reviewRate))}</p>
        <p className='rate'>{product?.reviewRate}</p>
        <p className='review'>
          리뷰 <span>{product.counter?.reviewCnt}건</span>
        </p>
      </div>

      {/* 가격, 쿠폰받기 버튼 */}
      <div className='priceinfo'>
        <div className='price'>
          {renderDiscountedPrice(product)}
          <div className='mileage_npay_wrap'>{renderMileage(product)}</div>
        </div>
        {hasAccessToken && couponSort?.length !== 0 && (
          <BtnCouponDownload onClick={() => setCouponDown(true)}>
            <img src={`${imgURL}/icon_download.png`} alt='다운로드' />
            쿠폰받기
          </BtnCouponDownload>
        )}
        {couponDown && (
          <ProductCouponCenterModal
            downloadable={couponSort}
            setCouponCheck={setCouponCheck}
            setIsModal={setCouponDown}
            headerText={'쿠폰받기'}
            selectProductNo={param.id}
          ></ProductCouponCenterModal>
        )}
      </div>

      {/* 할인 예상 정보 */}
      <ProductDiscountInfo product={product} productItem={productItem} disCountAcc={disCountAcc} />
    </div>
  )
}

export default memo(ProductPriceInfoPC)
